import React, { useState, useEffect, useRef } from "react";
import { API_BASE_URL } from "./constants/adminConstants";
import "./css/adminStyles.css";

function AdminOtpVerification({ userId, onVerified, onCancel }) {
  const [method, setMethod] = useState("email");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [timer, setTimer] = useState(0);
  const [confirmSwitch, setConfirmSwitch] = useState(null);
  const intervalRef = useRef(null);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (timer > 0) {
      intervalRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [timer]);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleSendOtp = async () => {
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, method }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to send OTP.");
        setLoading(false);
        return;
      }

      setOtpSent(true);
      setMessage(`OTP sent to your ${method}.`);
      setTimer(120);
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newOtp = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i++) {
      newOtp[i] = pasted[i];
    }
    setOtp(newOtp);
    const nextIdx = Math.min(pasted.length, 5);
    inputRefs.current[nextIdx]?.focus();
  };

  const handleVerifyOtp = async () => {
    setError("");
    setLoading(true);

    const otpString = otp.join("");

    try {
      const res = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, otp: otpString }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Verification failed.");
        setLoading(false);
        return;
      }

      if (onVerified) onVerified();
    } catch (err) {
      setError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    setTimer(0);
    handleSendOtp();
  };

  const handleMethodSwitch = (newMethod) => {
    if (otpSent && newMethod !== method) {
      setConfirmSwitch(newMethod);
    } else {
      setMethod(newMethod);
    }
  };

  const confirmMethodSwitch = () => {
    setMethod(confirmSwitch);
    setOtpSent(false);
    setOtp(["", "", "", "", "", ""]);
    setTimer(0);
    setMessage("");
    setError("");
    setConfirmSwitch(null);
  };

  return (
    <div className="AdminOtpSection" style={{ position: "relative" }}>
      {onCancel && (
        <button
          className="AdminOtpCloseBtn"
          onClick={onCancel}
          type="button"
        >
          x
        </button>
      )}
      <h3 className="AdminSubheading" style={{ margin: "0 0 12px 0", fontSize: 16 }}>
        Verify Your Account
      </h3>

      <div className="AdminOtpMethodToggle">
        <button
          className={`AdminToggleBtn ${method === "email" ? "AdminToggleBtnActive" : ""}`}
          onClick={() => handleMethodSwitch("email")}
        >
          Verify via Email
        </button>
        <button
          className={`AdminToggleBtn ${method === "phone" ? "AdminToggleBtnActive" : ""}`}
          onClick={() => handleMethodSwitch("phone")}
        >
          Verify via Phone
        </button>
      </div>

      {confirmSwitch && (
        <div className="AdminOtpSwitchConfirm" style={{ position: "relative" }}>
          <button className="AdminOtpSwitchClose" onClick={() => setConfirmSwitch(null)} type="button">x</button>
          <p className="AdminOtpSwitchText">The current OTP will not be valid for the new method.</p>
          <div className="AdminOtpSwitchActions">
            <button className="AdminButton AdminButtonSmall" onClick={confirmMethodSwitch}>Switch</button>
          </div>
        </div>
      )}

      {!otpSent ? (
        <button
          className="AdminButton AdminButtonSecondary AdminButtonSmall"
          onClick={handleSendOtp}
          disabled={loading}
        >
          {loading ? "Sending..." : `Send OTP to ${method}`}
        </button>
      ) : (
        <>
          <label className="AdminLabel" style={{ marginBottom: 12 }}>Enter OTP</label>
          <div className="AdminOtpBoxRow">
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                className="AdminOtpBox"
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={i === 0 ? handlePaste : undefined}
                autoFocus={i === 0}
              />
            ))}
          </div>

          {timer > 0 && (
            <p className="AdminOtpTimer">{formatTime(timer)}</p>
          )}

          <div className="AdminOtpBtnGroup">
            {timer === 0 && (
              <button
                className="AdminButton AdminButtonSecondary AdminButtonSmall"
                onClick={handleResend}
                disabled={loading}
              >
                Resend
              </button>
            )}

            <button
              className="AdminButton AdminButtonSmall"
              onClick={handleVerifyOtp}
              disabled={loading || otp.join("").length !== 6}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
          </div>
        </>
      )}

      {message && <p className="AdminSuccess">{message}</p>}
      {error && <p className="AdminError">{error}</p>}
    </div>
  );
}

export default AdminOtpVerification;
